import React, { useState, useEffect, useCallback, useRef } from 'react';
import useNotification from '../hooks/useNotification';
import axiosInstance from '../api/Axios';
import { API_ENDPOINTS } from '../constants/api';
import { formatDateTimeLocal } from '../utils/dateUtils';

// Fetches photo with auth as blob, displays via object URL, revokes on unmount; onClick opens in lightbox
const BugReportPhoto = ({ fileId, onClick }) => {
  const [objectUrl, setObjectUrl] = useState(null);
  const urlRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const url = `${API_ENDPOINTS.BUG_REPORTS}/photos/${encodeURIComponent(fileId)}`;
    axiosInstance.get(url, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        const blobUrl = URL.createObjectURL(res.data);
        urlRef.current = blobUrl;
        setObjectUrl(blobUrl);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
      setObjectUrl(null);
    };
  }, [fileId]);

  if (!objectUrl) return <span className="text-gray-400 text-xs">...</span>;
  return (
    <button
      type="button"
      className="block focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
      onClick={() => onClick?.(objectUrl)}
    >
      <img
        src={objectUrl}
        alt=""
        className="max-w-24 max-h-24 object-cover rounded border border-gray-200 hover:opacity-90"
      />
    </button>
  );
};

const Bugs = () => {
  const { showError } = useNotification();
  const [list, setList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lightboxUrl, setLightboxUrl] = useState(null);

  const fetchBugs = useCallback(async () => {
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.BUG_REPORTS);
      setList(res.data || []);
    } catch (error) {
      console.error('Error fetching bug reports:', error);
      showError('Ошибка при загрузке списка багов');
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchBugs();
  }, [fetchBugs]);

  const authorLabel = (item) => {
    const name = [item.fromFirstName, item.fromLastName].filter(Boolean).join(' ').trim();
    if (name) return name;
    if (item.fromUsername) return `@${item.fromUsername}`;
    return item.fromUserId ? `ID ${item.fromUserId}` : '—';
  };

  if (isLoading) {
    return (
      <div className="flex-1 ml-20 flex items-center justify-center bg-gray-200 min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="flex-1 ml-20 p-6 bg-gray-200 min-h-screen">
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
          role="dialog"
          aria-modal="true"
          onClick={() => setLightboxUrl(null)}
        >
          <button
            type="button"
            className="absolute top-4 right-4 text-white hover:text-gray-300 text-2xl leading-none"
            onClick={() => setLightboxUrl(null)}
            aria-label="Закрыть"
          >
            ×
          </button>
          <img
            src={lightboxUrl}
            alt=""
            className="max-h-[90vh] max-w-[90vw] object-contain pointer-events-none"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Баги (#баг)</h1>
          <p className="text-gray-600 mt-2">
            Сообщения с тегом #баг из Telegram
          </p>
        </div>
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {list.length === 0 ? (
            <div className="p-8 text-center text-gray-500">Нет записей</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">От кого</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Дата</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Текст</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Фото</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {list.map((item) => (
                    <tr key={item._id}>
                      <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{authorLabel(item)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                        {item.createdAt ? formatDateTimeLocal(item.createdAt) : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 max-w-md">{item.text || '—'}</td>
                      <td className="px-4 py-3">
                        {item.photoFileIds?.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {item.photoFileIds.map((fid) => (
                              <BugReportPhoto key={fid} fileId={fid} onClick={setLightboxUrl} />
                            ))}
                          </div>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Bugs;
